import { motion } from "framer-motion";
import {
  Menu,
  X,
  Terminal,
  Scissors,
  Copy,
  FileText,
  Book,
  Code,
  User,
  Languages,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useRouter } from "../context/RouterContext";
import { useLocale } from "../context/LocaleContext";
import api from "../services/api";
import { NavItemData, NavItem } from "../types";

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [siteName, setSiteName] = useState({ pt1: "DevOps", pt2: "by Cagla" });
  const [navItems, setNavItems] = useState<NavItem[]>([
    { name: "Home", page: "home", icon: Scissors },
    { name: "Articles", page: "articles", icon: FileText },
    { name: "Resources", page: "resources", icon: Book },
    { name: "Projects", page: "projects", icon: Code },
    { name: "About", page: "about", icon: User },
  ]);
  const { currentPage, navigate } = useRouter();
  const { locale, setLocale } = useLocale();

  useEffect(() => {
    async function fetchGlobalData() {
      try {
        //console.log("Fetching global data with locale:", locale);
        const response: any = await api.getGlobal(locale);
        //console.log("Global data response:", response);

        if (response?.data?.site_name) {
          const siteNameData = response.data.site_name;
          setSiteName({
            pt1: siteNameData.pt1 || "DevOps",
            pt2: siteNameData.pt2 || "by Cagla",
          });
        }

        if (response?.data?.nav_items) {
          const navItemsData: NavItemData = response.data.nav_items;
          //console.log("Nav items data:", navItemsData);
          const iconMap: Record<string, any> = {
            home: Scissors,
            categories: Copy,
            articles: FileText,
            resources: Book,
            projects: Code,
            about: User,
          };

          const apiNavItems: NavItem[] = Object.entries(navItemsData)
            .filter(([key]) => key !== "id" && key !== "__component")
            .map(([key, value]) => ({
              name: value as string,
              page: key.toLowerCase(),
              icon: iconMap[key.toLowerCase()] || Terminal,
            }));

          //console.log("Mapped nav items:", apiNavItems);
          if (apiNavItems.length > 0) {
            setNavItems(apiNavItems);
          }
        }
      } catch (error) {
        console.error("Failed to fetch global data:", error);
      }
    }

    fetchGlobalData();
  }, [locale]);

  const activeIndex = navItems.findIndex((item) => item.page === currentPage);
  const displayIndex = hoveredIndex !== null ? hoveredIndex : activeIndex;

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className="fixed top-0 left-0 right-0 z-50 bg-[#101010] backdrop-blur-md"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <motion.div
            whileHover={{ scale: 1.05 }}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("home")}
          >
            <Terminal className="w-8 h-8 text-red-600" />
            <span className="text-xl font-bold text-white">
              {siteName.pt1}
              <span className="text-red-600"> {siteName.pt2}</span>
            </span>
          </motion.div>

          <div className="flex items-center gap-4">
            {/* Locale Switcher */}
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setLocale(locale === "en" ? "tr-TR" : "en")}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-600/10 hover:bg-red-600/20 transition-colors border border-red-600/20"
            >
              <Languages className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-red-600 uppercase">
                {locale === "tr-TR" ? "TR" : "EN"}
              </span>
            </motion.button>

            <nav className="hidden md:flex items-center gap-0 relative">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = currentPage === item.page;
                const isHovered = hoveredIndex === index;
                return (
                  <motion.button
                    key={item.page}
                    onClick={() => navigate(item.page)}
                    onMouseEnter={() => setHoveredIndex(index)}
                    onMouseLeave={() => setHoveredIndex(null)}
                    className="relative flex flex-col items-center gap-1 px-6 py-3 z-10"
                    whileHover={{ y: -2 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Icon
                      className={`w-6 h-6 transition-colors duration-200 ${
                        isActive
                          ? "text-red-600"
                          : isHovered
                          ? "text-red-600"
                          : "text-gray-500"
                      }`}
                    />
                    <span
                      className={`text-[10px] font-medium transition-colors duration-200 ${
                        isActive
                          ? "text-red-600"
                          : isHovered
                          ? "text-red-600"
                          : "text-gray-500"
                      }`}
                    >
                      {item.name}
                    </span>
                  </motion.button>
                );
              })}
              {displayIndex >= 0 && (
                <motion.div
                  className="absolute bottom-0 h-[2px] bg-red-600"
                  initial={false}
                  animate={{
                    left: `${(displayIndex / navItems.length) * 100}%`,
                    width: `${100 / navItems.length}%`,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 380,
                    damping: 30,
                  }}
                />
              )}
            </nav>

            <button
              className="md:hidden text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <motion.nav
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden py-4 border-t border-red-900/20"
          >
            {navItems.map((item) => (
              <motion.button
                key={item.page}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  navigate(item.page);
                  setMobileMenuOpen(false);
                }}
                className={`block w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  currentPage === item.page
                    ? "bg-red-600 text-white"
                    : "text-gray-300 hover:text-white hover:bg-red-600/10"
                }`}
              >
                {item.name}
              </motion.button>
            ))}
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
}
